import React, {
  useMemo,
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Flex,
  Span,
  Table,
  Button,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  TextInput,
  PanelSlider,
  TableActions,
  useNotification,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import { useQuery, useQueryClient } from '@sopranodesign/pulse-hooks';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';

import { fetchZendeskTickets, updateZendeskTicketFilterName } from '../api';
import { StateLevel } from '.';

const ZendeskTicketsList = ({
  filterId,
  handleHide,
  onOpenTagsModal,
  onOpenRecrawlModal,
  loadingIds,
  errorIds
}) => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');
  const canEditTag = hasPermission('edit generative ai tag');
  const canEditResource = hasPermission('edit generative ai resource') && hasPermission('edit generative ai zendesk tickets');

  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  const [shouldRefetch, setShouldRefetch] = useState(false);

  const order = useOrderableTableReducer({ id: `zendesk-tickets-${filterId}` });
  const pagination = usePaginationReducer({ id: `zendesk-tickets-${filterId}` });

  const { data, isLoading } = useQuery({
    queryKey: [
      'generative-ai/zendesk/tickets',
      filterId,
      { ...pagination.params, ...order.params }
    ],
    queryFn: () => fetchZendeskTickets(filterId, { ...pagination.params, ...order.params }),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const response = useMemo(() => data?.data ?? {}, [data]);
  const tickets = useMemo(() => response.data ?? [], [response]);
  const meta = response.meta ?? {};
  const filterName = response.filter_name ?? 'Tickets';

  const globalHasPending = response.has_pending ?? false;

  useEffect(() => {
    setShouldRefetch(globalHasPending);
  }, [globalHasPending]);

  const startEditingName = () => {
    setNameDraft(filterName);
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
    setNameDraft('');
  };

  const saveFilterName = async () => {
    const name = nameDraft.trim();
    if (!name || name === filterName) {
      cancelEditingName();
      return;
    }

    setIsSavingName(true);
    try {
      await updateZendeskTicketFilterName(filterId, name);
      queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/tickets'] });
      showSuccess('Filter renamed successfully!');
      setIsEditingName(false);
    } catch (error) {
      showError(error.response?.data?.error ?? 'Failed to rename filter.');
    } finally {
      setIsSavingName(false);
    }
  };

  const panelHeader = isEditingName ? (
    <Flex center gap="sm">
      <TextInput
        autoFocus
        value={nameDraft}
        disabled={isSavingName}
        onChange={e => setNameDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { saveFilterName(); }
          if (e.key === 'Escape') { cancelEditingName(); }
        }}
      />
      <Button icon="check" loading={isSavingName} onClick={saveFilterName}>Save</Button>
      <Button icon="x" variant="secondary" disabled={isSavingName} onClick={cancelEditingName}>Cancel</Button>
    </Flex>
  ) : (
    <Flex center gap="sm">
      <Span>{filterName}</Span>
      <PermissionFilter can="edit generative ai zendesk tickets">
        <Button icon="pencil" variant="secondary" onClick={startEditingName}>Rename</Button>
      </PermissionFilter>
    </Flex>
  );

  return (
    <AnimatePresence>
      <PanelSlider header={panelHeader} handleHide={handleHide}>
        {isLoading && <LoadingContainer />}
        {!isLoading && !tickets.length && (
          <Paragraph mb secondary>No tickets available.</Paragraph>
        )}
        {!isLoading && tickets.length > 0 && (
          <Flex col>
            <Flex border xScroll>
              <Table>
                <TableHead>
                  <OrderableTableRow
                    rows={[
                      { label: 'Subject', sort: 'subject' },
                      { label: 'Status', sort: 'status' },
                      { label: 'Crawled At', sort: 'updated_at' },
                      ...(canViewTag ? [{ label: 'Tags' }] : []),
                      ...(((canViewTag && canEditTag) || canEditResource) ? [null] : [])
                    ]}
                    {...order.props}
                  />
                </TableHead>
                <TableBody>
                  {tickets.map(ticket => (
                    <TableRow key={ticket.id}>
                      <TableCell>{ticket.subject}</TableCell>
                      <TableCell>
                        <Flex center>
                          <StateLevel level={ticket.status} />
                          <span>{ticket.status}</span>
                        </Flex>
                      </TableCell>
                      <TableCell>{ticket.updated_at}</TableCell>

                      <PermissionFilter can="view generative ai tag">
                        <TableCell>
                          {ticket.groups && ticket.groups.length > 0
                            ? ticket.groups.map(group => group.name).join(', ')
                            : 'No Tags'}
                        </TableCell>
                      </PermissionFilter>

                      {((canViewTag && canEditTag) || canEditResource) && (
                        <TableActions>
                          <PermissionFilter can="view generative ai tag">
                            <PermissionFilter can="edit generative ai tag">
                              {ticket.status === 'Success' && (
                                <Button
                                  icon="tag"
                                  onClick={() => onOpenTagsModal(ticket)}
                                >
                                  Tags
                                </Button>
                              )}
                            </PermissionFilter>
                          </PermissionFilter>

                          <PermissionFilter can="edit generative ai resource">
                            <PermissionFilter can="edit generative ai zendesk tickets">
                              <Button
                                icon="download"
                                loading={Boolean(loadingIds?.[ticket.id])}
                                disabled={globalHasPending}
                                onClick={() => onOpenRecrawlModal(ticket)}
                              >
                                Recrawl
                              </Button>
                            </PermissionFilter>
                          </PermissionFilter>

                          {errorIds?.[ticket.id] && (
                            <p style={{ color: 'red', marginTop: '4px' }}>
                              {errorIds[ticket.id]}
                            </p>
                          )}
                        </TableActions>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>

            {typeof meta.current_page === 'number' && (
              <Pagination pagination={meta} {...pagination.props} />
            )}
          </Flex>
        )}
      </PanelSlider>
    </AnimatePresence>
  );
};

ZendeskTicketsList.propTypes = {
  filterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleHide: PropTypes.func.isRequired,
  onOpenTagsModal: PropTypes.func.isRequired,
  onOpenRecrawlModal: PropTypes.func.isRequired,
  loadingIds: PropTypes.object,
  errorIds: PropTypes.object.isRequired
};

export default ZendeskTicketsList;
